import React, { useState, KeyboardEvent } from 'react';
import { X } from 'lucide-react';

interface TagInputProps {
  label: string;
  value: string[];
  onSave: (tags: string[]) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  maxTags?: number;
  suggestions?: string[];
}

export const TagInput: React.FC<TagInputProps> = ({
  label,
  value = [],
  onSave,
  disabled = false,
  placeholder = "Tapez et appuyez sur Entrée pour ajouter",
  maxTags,
  suggestions = []
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [tempTags, setTempTags] = useState<string[]>(value);

  const startEditing = () => {
    if (disabled) return;
    setIsEditing(true);
    setTempTags([...value]);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setTempTags([...value]);
    setInputValue('');
  };

  const saveChanges = async () => {
    await onSave(tempTags);
    setIsEditing(false);
    setInputValue('');
  };

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !tempTags.includes(trimmedTag)) {
      if (maxTags && tempTags.length >= maxTags) return;
      setTempTags([...tempTags, trimmedTag]);
      setInputValue('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTempTags(tempTags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    addTag(suggestion);
  };

  const filteredSuggestions = suggestions.filter(
    suggestion => 
      suggestion.toLowerCase().includes(inputValue.toLowerCase()) &&
      !tempTags.includes(suggestion)
  );

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {maxTags && (
          <span className="text-xs text-gray-500 ml-1">
            ({value.length}/{maxTags})
          </span>
        )}
      </label>

      {!isEditing ? (
        // Mode affichage
        <div
          onClick={startEditing}
          className={`
            min-h-[2.5rem] p-2 border border-gray-300 rounded-lg
            ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}
            transition-colors duration-200
          `}
        >
          {value.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {value.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 bg-[#197ca8] text-white text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-gray-400 text-sm">{placeholder}</span>
          )}
        </div>
      ) : (
        // Mode édition
        <div className="space-y-2">
          <div className="border border-gray-300 rounded-lg p-2 focus-within:ring-2 focus-within:ring-[#197ca8] focus-within:border-[#197ca8]">
            {/* Tags existants */}
            {tempTags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {tempTags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 bg-[#197ca8] text-white text-xs rounded-full"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-1 hover:bg-[#156a92] rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Input pour ajouter des tags */}
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={tempTags.length === 0 ? placeholder : "Ajouter un autre..."}
              className="w-full border-none outline-none text-sm"
              disabled={maxTags ? tempTags.length >= maxTags : false}
            />
          </div>

          {/* Suggestions */}
          {filteredSuggestions.length > 0 && inputValue && (
            <div className="border border-gray-200 rounded-lg bg-white shadow-sm max-h-32 overflow-y-auto">
              {filteredSuggestions.slice(0, 5).map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}

          {/* Boutons d'action */}
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={cancelEditing}
              className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={saveChanges}
              className="px-3 py-1 text-sm bg-[#197ca8] text-white rounded hover:bg-[#156a92]"
            >
              Sauvegarder
            </button>
          </div>
        </div>
      )}
    </div>
  );
};